import { bootcampSplitApi } from '~/bootcamp/apis/split.api'
import { homApi } from '~/redux/apis/hom'
import { LearningStatsAPI, RewardsStats } from '~/statistics/types/profile-card.types'
import { StackNLoadProductAPIStat, TreasureTrailProductAPIStat } from '~/statistics/types/stack-n-load.types'
import { Report } from '~/user-stats/types/user-stats.types'
import { VideoStat } from '~/user-stats/types/video-stats'
import { WindSurferProductStatAPI } from '~/statistics/types/wind-surfer.types'

export interface DateFilters {
  startDate: string | undefined
  endDate: string | undefined
  familyMemberId?: number
  category?: string
}

/**
 * Games scope. Neither field means the lifetime read the all-math panel wants; `curriculum` covers
 * every grade of one curriculum, `grade` a single one. Rows with no stamp (played before it existed,
 * or by a user with no grade selected) fall out of both by design.
 */
export interface GamesStatsFilters {
  familyMemberId?: number
  curriculum?: string
  grade?: string
}

interface PageViewStatsQuery {
  from?: string
  to?: string
  user?: string
}

export interface TimeSpentStatsQuery {
  from?: string
  to?: string
  familyMemberId?: number
}

export interface WeeklyTimeSpentStatsQuery {
  dateTime: string
  timezone: string
}

export interface ExerciseStatsResponse {
  correctAnswers: number
  wrongAnswers: number
  bestActivityAccuracy: number
  noActivities: boolean
}

export interface VideoStatInsightResponse {
  summary: {
    watchedVideoCount: number
    watchedVideoDuration: number
  }
  videos: VideoStat[]
}

export interface ActivityChartStatstQuery {
  product: string
  activityPath: string
}

export interface ActivityChartStatstResponse {
  attempts: [
    {
      accuracy: number
      date: string
    },
  ]
}

export interface ActivityRoundRequest {
  activityPath: string
  start?: boolean
  end?: boolean
  roundId?: string
  product: string
}

export interface ActivityRoundResponse {
  completedAt: null | string
  createdAt: string
  id: number
  productId: string
  roundId: string
  userId: number
}

export interface VideoLinks {
  path: string
  isWatched: boolean
}

export interface BootcampVideoStat {
  watchedCount: number
  totalCount: number
  watchedDuration: number
  videoLinks: VideoLinks[]
}

export interface BootcampLevelActivitySetStat {
  activityPath: string
  chestsCount: number
  completedChestsCount: number
  correctAnswers: number
  incorrectAnswers: number
  retakeRound: string | null
}

export interface BootcampLevelStat {
  activitiesCount: number
  activitySets: { [key: string]: BootcampLevelActivitySetStat }
  isCompleted: boolean
  name: string
}

export const enum ProductCardTypes {
  BOOTCAMP = 'bootcamp',
  MATHVAULT = 'mathvault',
  MATHESSENTIAL = 'mathEssential',
  STACKNLOAD = 'stackNload',
  TREAURETRAIL = 'treasureTrail',
  WINDSURFER = 'windSurfer',
  MULT_GAME = 'multiplicationMaster',
  ENCYCLOPEDIA = 'encyclopedia',
  CURRICULUM_VIDEO = 'curriculumVideo',
  CURRICULUM_ARTICLE = 'curriculumArticle',
  TEST_PREP = 'testPrep',
}

export interface CurriculumArticleProductUIStat {
  id: string
  subjectTitle: string
  title: string
  path: string
  timeSpent: DaysMinsHours
  activitiesSummary: {
    totalSetsCount: number
    setsCompletedCount: number
  }
  articleRead: boolean
  totalAnswered: number
  correctAnswerPercentage: number
  activitySetsProgress: number
}

export interface ArticleProductAPIStat {
  id: string
  type: string
  subjectTitle: string
  title: string
  activitySets: {
    [activitySetId: string]: {
      chestsCount: number
      completedChestsCount: number
    }
  }
  path: string
  lastVisited: string
  timeSpent: number
  summary: {
    setsCompletedCount: number
    totalSetsCount: number
    totalCorrectAnswersCount: number
    totalIncorrectAnswersCount: number
  }
  articleRead: boolean
}

export interface CurriculumVideoProductAPIStat {
  id: string
  lastVisited: string
  timeSpent: number
  type: ProductCardTypes.CURRICULUM_VIDEO
  subjectTitle: string
  title: string
  summary: {
    setsCompletedCount: number
    totalCorrectAnswersCount: number
    totalIncorrectAnswersCount: number
    totalSetsCount: number
  }
  video: BootcampVideoStat
  path: string
}

export interface BootcampProductAPIStat {
  id: string
  lastVisited: string
  timeSpent: number
  type: ProductCardTypes.BOOTCAMP
  subjectTitle: string
  supportsCurrentLocale: boolean
  videoCourseName: string
  summary: {
    completedBossBattlesCount: number
    completedChestsCount: number
    correctAnswersCount: number
    incorrectAnswersCount: number
    setsCompletedCount: number
    totalBossBattlesCount: number
    totalChestsCount: number
    totalCorrectAnswersCount: number
    totalIncorrectAnswersCount: number
    totalSetsCount: number
  }
  video: BootcampVideoStat
  firstLevelName: string
  bossBattleUrl: string
  levels: { [key: string]: BootcampLevelStat }
}

export interface DaysMinsHours {
  days: number
  hours: number
  minutes: number
}

export interface CurriculumVideoProductUIStat {
  id: string
  subjectTitle: string
  title: string
  timeSpent: DaysMinsHours
  activitiesSummary: {
    totalSetsCount: number
    setsCompletedCount: number
  }
  videoProgress: DaysMinsHours
  video: { totalCount: number; watchedCount: number; watchedDuration: number }
  videoProgressBarData: Array<{ value: number; color: string }>
  totalAnswered: number
  productHref: string
  correctAnswerPercentage: number
  activitySetsProgress: number
}

export interface BootcampProductUIStat {
  id: string
  subjectTitle: string
  videoCourseName: string
  bossBattleUrl: string
  timeSpent: DaysMinsHours
  activitiesSummary: {
    completedBossBattlesCount: number
    totalBossBattlesCount: number
    totalSetsCount: number
    setsCompletedCount: number
  }
  supportsCurrentLocale: boolean
  correctAnswerPercentage: number
  isBossBattleComplete: boolean
  progressBarPercentage: number
  activitySetsProgress: number
  videoProgress: DaysMinsHours
  video: { totalCount: number; watchedCount: number; watchedDuration: number }
  levels: { [key: number]: BootcampLevelStat }
  videoProgressBarData: Array<{ value: number; color: string }>
  totalAnswered: number
  productHref: string
}

export interface MathVaultLevelStat {
  completedActivitiesCount: number
  totalActivitiesCount: number
  levelNo: number
}

export interface MathVaultProductAPIStat {
  id: string
  lastVisited: string
  timeSpent: number
  type: ProductCardTypes.MATHVAULT
  room: string
  roomColor: string
  chest: string
  chestPath: string
  supportsCurrentLocale?: boolean
  summary: {
    completedActivitiesCount: number
    totalActivitiesCount: number
    completedLevelsCount: number
    totalLevelsCount: number
  }
  levels: {
    [key: string]: MathVaultLevelStat
  }
  nextLevel: number | null
  nextQuestion: number | null
}

export interface MathEssentialActivityStat {
  id: number
  title: string
  type: string
  isCompleted?: boolean | null
  stars?: number
  accuracy?: number
  quizNo?: number
  questionsCount?: number
}

export interface MathEssentialProductDetails {
  completedQuizesCount: number
  totalQuizesCount: number
  completedArticlesCount: number
  totalArticlesCount: number
  activityStats: MathEssentialActivityStat[]
}

export interface MathEssentialProductAPIStat {
  id: string
  type: ProductCardTypes.MATHESSENTIAL
  subject: string
  subjectPath: string
  subjectKey: string
  lastVisited: string
  timeSpent: number
  serial: number
  supportsCurrentLocale?: boolean
  summary: {
    totalCorrectInBestAttempts: number
    totalInCorrectInBestAttempts: number
    progressPercentage: number
  }
  details: MathEssentialProductDetails
  continueActivity?: string
}

export interface MultiplicationMasterSummary {
  totalActivitiesSolvedCount: number
  accuracy: number
}

export interface MultiplicationMasterSprintAndSurvival {
  title: string
  accuracy: number
  bestStreak: number
  score: number
}

export interface MultiplicationMasterTableStats {
  [key: string]: {
    accuracy: number
    score: number
  }
}

export interface MultiplicationMasterTableMaster {
  title: string
  tables: MultiplicationMasterTableStats
}

export interface MultiplicationMasterDetails {
  sprint: MultiplicationMasterSprintAndSurvival
  survival: MultiplicationMasterSprintAndSurvival
  tablesMaster: MultiplicationMasterTableMaster
}

export interface MultiplicationMasterAPIStat {
  id: string
  type: ProductCardTypes.MULT_GAME
  timeSpent: number
  lastVisited: string
  summary: MultiplicationMasterSummary
  details: MultiplicationMasterDetails
}

export interface TestPrepProductUIStat {
  id: string
  type: ProductCardTypes.TEST_PREP
  gradeTitle: string
  curriculumCountryCode: string
  timeSpent: DaysMinsHours
  scoreSummary: {
    totalQuestionsCount: number
    answeredQuestionsCount: number
    correctAnswersCount: number
  }
  testBuildUrl: string
  correctnessPercentage: number
  levelsByDate: { [key: string]: TestPrepLevelStat }
  lastVisited: string
  shouldShowCurriculum?: boolean
}

export interface TestPrepProductAPIStat {
  id: string
  type: ProductCardTypes.TEST_PREP
  gradeTitle: string
  curriculumCountryCode: string
  timeSpentInSec: number
  scoreSummary: {
    totalQuestionsCount: number
    answeredQuestionsCount: number
    correctAnswersCount: number
  }
  levelsByDate: { [key: string]: TestPrepLevelStat }
  lastVisited: string
  shouldShowCurriculum?: boolean
}

export interface TestPrepLevelStat {
  date: string
  testSet: TestSetState[]
}

export interface TestSetState {
  correctnessPercentage: number
  examId: number
  testIncrementalId: number
  allScores: Array<{ date: string; correctnessPercentage: number }>
}

/**
 * What games add to the headline figures. `activities` counts every attempt; `latestAttempted` and
 * `latestCorrect` cover only the most recent attempt of each thing played, so a learner who retries
 * a puzzle until it is right reads as 100% while the earlier tries still count as activities.
 *
 * Covers Wind Surfer, Treasure Trail and Multiplication Master. Stack'n Load stores no correctness
 * data, so it contributes nothing here however much of it was played.
 */
export interface GameActivityTotals {
  activities: number
  latestAttempted: number
  latestCorrect: number
  /** XP the games awarded, from the reward ledger — the game tables never see it. */
  xp: number
}

export interface GamesStats {
  windSurfer: WindSurferProductStatAPI
  treasureTrail: TreasureTrailProductAPIStat
  stacknLoad: StackNLoadProductAPIStat
  multGame: MultiplicationMasterAPIStat
  activityTotals: GameActivityTotals
}

export type ProductCardsType =
  | BootcampProductAPIStat
  | ArticleProductAPIStat
  | MathVaultProductAPIStat
  | MathEssentialProductAPIStat
  | StackNLoadProductAPIStat
  | TreasureTrailProductAPIStat
  | WindSurferProductStatAPI
  | MultiplicationMasterAPIStat
  | CurriculumVideoProductAPIStat
  | TestPrepProductAPIStat

export type ProductsApiStats = ProductCardsType[]

export interface UserTimeSpent {
  viewKey: string
  timeSpent: number
  shouldUpdate?: boolean
}

export const userApi = homApi.injectEndpoints({
  endpoints: (builder) => ({
    timeSpentStatsDevInsight: builder.query<Report, PageViewStatsQuery>({
      query: (params) => ({
        url: '/v1/user-stats/_dev/insight-report',
        method: 'GET',
        params,
      }),
    }),

    videoStatsDevInsight: builder.query<VideoStatInsightResponse, DateFilters>({
      query: (params) => ({
        url: '/v1/user-stats/_dev/video-insight-report',
        method: 'GET',
        params,
      }),
    }),

    timeSpentStats: builder.query<{ timeSpent: number }, TimeSpentStatsQuery>({
      query: ({ familyMemberId }) => ({
        url: `/v1/user-stats/time-spent${familyMemberId ? `?familyMemberId=${familyMemberId}` : ''}`,
        method: 'GET',
      }),
    }),

    timeSpentWeeklyStats: builder.query<
      { weekNumber: number; average: number; uses: number[] },
      WeeklyTimeSpentStatsQuery
    >({
      query: (params) => ({
        url: '/v1/user-stats/time-spent-weekly',
        method: 'GET',
        params,
      }),
    }),

    learningStats: builder.query<LearningStatsAPI, DateFilters>({
      query: (params) => ({
        url: '/v1/user-stats/learning',
        method: 'GET',
        params,
      }),
      keepUnusedDataFor: 0, // Disable caching
    }),

    rewardStats: builder.query<RewardsStats, DateFilters>({
      query: (params) => ({
        url: '/v1/reward/user-stats',
        method: 'GET',
        params,
      }),
    }),

    productStats: builder.query<ProductsApiStats, DateFilters>({
      query: (params) => ({
        url: '/v1/user-stats/products',
        method: 'GET',
        params,
      }),
    }),

    gamesStats: builder.query<GamesStats, GamesStatsFilters>({
      query: (params) => ({
        url: '/v1/user-stats/games',
        method: 'GET',
        params,
      }),
    }),

    exerciseStats: builder.query<ExerciseStatsResponse, undefined>({
      query: () => ({
        url: '/v1/activity/answers',
        method: 'GET',
      }),
    }),

    activityChartStats: builder.query<ActivityChartStatstResponse, ActivityChartStatstQuery>({
      query: (params) => ({
        url: '/v1/user-stats/rounds',
        method: 'GET',
        params,
      }),
    }),
    setActivityRound: builder.mutation<ActivityRoundResponse, ActivityRoundRequest>({
      query: (body) => ({
        url: '/v1/user-stats/rounds',
        method: 'POST',
        body,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled
        dispatch(bootcampSplitApi.util.invalidateTags(['BestAccuracy', 'ActivitySetAccuracy']))
      },
    }),

    userTimeSpentByViewKeys: builder.mutation<UserTimeSpent[], { viewKeys: string[] }>({
      query: (body) => ({
        url: '/v1/user-stats/user-time-by-view-keys',
        method: 'POST',
        body,
      }),
    }),
    updateUserTimeSpentByViewKeys: builder.mutation<
      UserTimeSpent[],
      { values: Array<{ viewKey: string; timeSpent: number }> }
    >({
      query: (body) => ({
        url: '/v1/user-stats/user-time-by-view-keys',
        method: 'PUT',
        body,
      }),
    }),
    getUnMappedActivitiesStats: builder.query<
      { totalAttempt: number; totalCorrect: number },
      void | { familyMemberId?: number }
    >({
      query: (params) => ({
        url: `/v1/user-stats/unmapped-activity-stats${
          params?.familyMemberId ? `?familyMemberId=${params.familyMemberId}` : ''
        }`,
        method: 'GET',
      }),
    }),
  }),
})

export const { learningStats } = userApi.endpoints

export const {
  useTimeSpentStatsQuery,
  useTimeSpentWeeklyStatsQuery,
  useVideoStatsDevInsightQuery,
  useTimeSpentStatsDevInsightQuery,
  useExerciseStatsQuery,
  useLearningStatsQuery,
  useRewardStatsQuery,
  useProductStatsQuery,
  useGamesStatsQuery,
  useActivityChartStatsQuery,
  useSetActivityRoundMutation,
  useUserTimeSpentByViewKeysMutation,
  useUpdateUserTimeSpentByViewKeysMutation,
  useGetUnMappedActivitiesStatsQuery,
} = userApi
